import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchPublicCatalogListingBySlug, PublicCatalogListing } from '@/services/catalog';
import { parseDatabaseError } from '@/lib/errors';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import {
  Store,
  ArrowLeft,
  PhoneCall,
  Laptop as LaptopIcon,
  Package,
  Loader2,
  Share2,
  Copy,
  Check,
} from 'lucide-react';

function formatPriceRange(min: number | null, max: number | null): string {
  if (min == null && max == null) return 'Call for Price';
  if (min != null && max != null && min === max) {
    return `Rs. ${min.toLocaleString('en-PK')}`;
  }
  if (min != null && max != null) {
    return `Rs. ${min.toLocaleString('en-PK')} – ${max.toLocaleString('en-PK')}`;
  }
  return `Rs. ${(min ?? max ?? 0).toLocaleString('en-PK')}`;
}

export const ShopDetailPage: React.FC = () => {
  const { slug = '', id = '' } = useParams<{ slug?: string; type?: string; id?: string }>();
  const lookupKey = slug || id;
  const [copied, setCopied] = useState(false);

  const { data: item, isLoading, isError, error } = useQuery<PublicCatalogListing | null>({
    queryKey: ['public_catalog_detail', lookupKey],
    queryFn: () => fetchPublicCatalogListingBySlug(lookupKey),
    enabled: !!lookupKey,
  });

  const shareUrl = window.location.href;

  const handleCopyLink = () => {
    void navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleNativeShare = async () => {
    if (navigator.share && item) {
      try {
        await navigator.share({
          title: `${item.title} — Al Kuwait Tech`,
          text: `Check out ${item.title} at Al Kuwait Tech!`,
          url: shareUrl,
        });
      } catch (e) {
        // User cancelled or share not supported
      }
    } else {
      handleCopyLink();
    }
  };

  const whatsappMessage = item
    ? encodeURIComponent(
        `Hi Al Kuwait Tech! I saw "${item.title}" on your online catalog (${shareUrl}). Is this unit currently available?`
      )
    : '';

  return (
    <div className="min-h-screen bg-brand-bg flex flex-col font-sans text-xs">
      {/* Header */}
      <header className="bg-white border-b border-brand-border sticky top-0 z-30 px-4 sm:px-8 py-3 flex items-center justify-between shadow-xs">
        <div className="flex items-center space-x-3">
          <Link
            to="/shop"
            className="w-9 h-9 rounded-lg bg-brand-green/10 flex items-center justify-center text-brand-green font-extrabold text-base"
          >
            AK
          </Link>
          <div>
            <h1 className="font-bold text-sm text-brand-black tracking-tight uppercase">
              AL KUWAIT TECH
            </h1>
            <p className="text-[10px] text-brand-muted">Laptops & Accessories Store</p>
          </div>
        </div>

        <Link
          to="/shop"
          className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-brand-bg hover:bg-slate-200 text-brand-black font-semibold text-xs rounded-md transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Back to Catalogue</span>
        </Link>
      </header>

      {/* Main Detail Container */}
      <main className="flex-1 p-4 sm:p-8 max-w-5xl mx-auto w-full">
        {isError && <ErrorAlert message={parseDatabaseError(error)} />}

        {isLoading && (
          <div className="flex flex-col items-center justify-center py-20 text-center space-y-2">
            <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
            <p className="text-brand-muted font-medium">Loading product specifications…</p>
          </div>
        )}

        {!isLoading && !item && (
          <div className="bg-white rounded-xl border border-brand-border p-12 text-center space-y-3">
            <Store className="w-12 h-12 text-brand-muted/30 mx-auto" />
            <h2 className="text-lg font-bold text-brand-black">Product Not Found or Unpublished</h2>
            <p className="text-xs text-brand-muted max-w-md mx-auto">
              This product listing is no longer active or the URL has changed. Browse our latest available inventory in the catalog.
            </p>
            <Link
              to="/shop"
              className="inline-flex items-center space-x-1 px-4 py-2 bg-brand-green text-white font-bold rounded-md"
            >
              Browse All Products
            </Link>
          </div>
        )}

        {item && !isLoading && (
          <div className="bg-white rounded-xl border border-brand-border p-6 sm:p-8 shadow-xs grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Image Column */}
            <div className="space-y-4">
              <div className="h-72 sm:h-96 bg-brand-bg rounded-lg border border-brand-border flex items-center justify-center p-6 relative">
                {item.cover_image_path ? (
                  <img
                    src={item.cover_image_path}
                    alt={item.title}
                    className="h-full max-w-full object-contain"
                  />
                ) : (
                  <div className="text-brand-muted flex flex-col items-center">
                    {item.listing_type === 'laptop_group' ? (
                      <LaptopIcon className="w-20 h-20 opacity-30 mb-2" />
                    ) : (
                      <Package className="w-20 h-20 opacity-30 mb-2" />
                    )}
                    <span className="text-xs">Certified Al Kuwait Tech Product</span>
                  </div>
                )}

                {item.touchscreen === true && (
                  <span className="absolute top-3 left-3 px-2.5 py-1 bg-blue-600 text-white rounded font-bold uppercase text-[10px] shadow-xs">
                    Touchscreen Model
                  </span>
                )}
              </div>

              {/* Share & Copy Buttons */}
              <div className="flex items-center justify-between pt-2">
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="inline-flex items-center space-x-1.5 px-3 py-1.5 border border-brand-border rounded-md text-brand-black hover:bg-brand-bg font-semibold transition-colors text-xs"
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-brand-green" /> : <Copy className="w-3.5 h-3.5" />}
                  <span>{copied ? 'Link Copied!' : 'Copy Link'}</span>
                </button>

                <button
                  type="button"
                  onClick={handleNativeShare}
                  className="inline-flex items-center space-x-1.5 px-3 py-1.5 border border-brand-border rounded-md text-brand-black hover:bg-brand-bg font-semibold transition-colors text-xs"
                >
                  <Share2 className="w-3.5 h-3.5 text-brand-green" />
                  <span>Share Product</span>
                </button>
              </div>
            </div>

            {/* Product Specifications & WhatsApp CTA Column */}
            <div className="space-y-5 flex flex-col justify-between">
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <span
                    className={`px-2.5 py-0.5 rounded font-bold uppercase text-[10px] ${
                      item.listing_type === 'laptop_group'
                        ? 'bg-brand-green text-white'
                        : 'bg-blue-600 text-white'
                    }`}
                  >
                    {item.listing_type === 'laptop_group' ? 'Laptop Model' : 'Accessory / Part'}
                  </span>
                  <span
                    className={`px-2.5 py-0.5 rounded font-bold uppercase text-[10px] ${
                      item.available_quantity > 0
                        ? 'bg-green-100 text-green-800'
                        : 'bg-slate-100 text-slate-700'
                    }`}
                  >
                    {item.available_quantity > 0
                      ? `${item.available_quantity} Units in Stock`
                      : 'Available on Order'}
                  </span>
                </div>

                <h1 className="text-2xl font-extrabold text-brand-black tracking-tight">
                  {item.title}
                </h1>

                {/* Price Box */}
                <div className="p-4 bg-green-50/70 border border-brand-green/30 rounded-lg">
                  <p className="text-[10px] uppercase font-bold text-brand-muted">Offer Price (PKR)</p>
                  <p className="text-2xl font-extrabold text-brand-green mt-0.5">
                    {formatPriceRange(item.min_price, item.max_price)}
                  </p>
                  <p className="text-[10px] text-brand-muted mt-1">
                    * Final price depends on chosen RAM/SSD upgrade configuration.
                  </p>
                </div>

                {/* Hardware Specifications Pills */}
                {item.listing_type === 'laptop_group' && (
                  <div className="space-y-3 pt-2">
                    <h3 className="text-xs font-bold text-brand-black uppercase tracking-wider">
                      Available Configurations & Hardware
                    </h3>

                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {item.cpu && (
                        <div className="p-2.5 bg-brand-bg rounded border border-brand-border">
                          <p className="text-[10px] font-bold text-brand-muted uppercase">Processor (CPU)</p>
                          <p className="font-bold text-brand-black mt-0.5">{item.cpu}</p>
                        </div>
                      )}

                      <div className="p-2.5 bg-brand-bg rounded border border-brand-border">
                        <p className="text-[10px] font-bold text-brand-muted uppercase">Display</p>
                        <p className="font-bold text-brand-black mt-0.5">
                          {item.touchscreen ? 'Touchscreen Display' : 'Non-Touch Display'}
                        </p>
                      </div>
                    </div>

                    {/* RAM Options */}
                    {item.ram_variants && item.ram_variants.length > 0 && (
                      <div>
                        <p className="text-[10px] font-bold text-brand-muted uppercase mb-1">
                          RAM Configurations in Stock:
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {item.ram_variants.map((r, i) => (
                            <span
                              key={i}
                              className="px-2.5 py-1 bg-white border border-brand-border rounded font-bold text-brand-black shadow-2xs"
                            >
                              {r}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Storage Options */}
                    {item.storage_variants && item.storage_variants.length > 0 && (
                      <div>
                        <p className="text-[10px] font-bold text-brand-muted uppercase mb-1">
                          Storage Options:
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {item.storage_variants.map((st, i) => (
                            <span
                              key={i}
                              className="px-2.5 py-1 bg-white border border-brand-border rounded font-bold text-brand-black shadow-2xs"
                            >
                              {st}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* GPU Options */}
                    {item.gpu_variants && item.gpu_variants.length > 0 && (
                      <div>
                        <p className="text-[10px] font-bold text-brand-muted uppercase mb-1">
                          Graphics (GPU):
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {item.gpu_variants.map((g, i) => (
                            <span
                              key={i}
                              className="px-2.5 py-1 bg-white border border-brand-border rounded font-medium text-brand-muted shadow-2xs"
                            >
                              {g}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Description */}
                {item.description && (
                  <div className="space-y-1 pt-2 border-t border-brand-border">
                    <p className="text-[10px] uppercase text-brand-muted font-bold">About this Product</p>
                    <p className="text-xs text-brand-black leading-relaxed whitespace-pre-line">
                      {item.description}
                    </p>
                  </div>
                )}
              </div>

              {/* Purchase Call to Action */}
              <div className="pt-4 border-t border-brand-border space-y-2">
                <a
                  href={`https://wa.me/923000000000?text=${whatsappMessage}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-3 bg-[#25D366] hover:bg-[#1EBE5D] text-white text-sm font-extrabold rounded-lg flex items-center justify-center space-x-2 transition-colors shadow-md text-center"
                >
                  <PhoneCall className="w-4 h-4" />
                  <span>Enquire & Order via WhatsApp</span>
                </a>
                <p className="text-[10px] text-center text-brand-muted">
                  Instant confirmation with our sales team. Cash on Delivery available across major cities.
                </p>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-brand-border p-6 text-center text-brand-muted space-y-1">
        <p className="font-bold text-brand-black">AL KUWAIT TECH — Retail & Repair Service Store</p>
        <p>© {new Date().getFullYear()} All Rights Reserved.</p>
      </footer>
    </div>
  );
};
